import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { ThemeProvider } from '@mui/material/styles'
import { useEffect, useRef, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import InAppSpy from 'inapp-spy'
import { FontLoader } from '@repo/fonts'
import { authenticationState, useAuthentication, useDictionary, languageState, countryState, type TCountry, type TCountryFeelgreat } from '@repo/unicity'
import AppRouter from './core/router/Router'
import { feelGreatTheme } from './core/theme'
import { EnvironmentSwitcher } from './core/components'
import { ErrorBoundary } from './core/components/error-boundary.component'
import LoadingBar from './core/components/Loader/loading-bar.component'
import { directGet, getFeelGreatCountriesUrl, ORDERCALC_COUNTRIES } from './core/http'
import {
    feelgreatCountriesAtom,
    isLoadingFeelgreatCountriesAtom,
    allFeelgreatCountriesAtom,
    feelgreatCountryAtom,
    type TFeelGreatCountriesResponse
} from './core/store/country.store'
import { feelgreatSettingsAtom, isFeelGreatLoadingAtom } from './core/feelgreat/feelgreat.store'
import { fetchFeelGreatSettings } from './core/feelgreat/feelgreat.api'
import { readStoredUserStatus } from '@/core/auth/user-status.store'
import { selectedFlavorAtom, selectedPurchaseOptionAtom } from './core/products/products.store'
import { setApiLoadedAtom, isWebReadyAtom } from './core/store/startup.store'
import { analytics } from './core/analytics/analytics'

let hasOpenedExternalBrowser = false

function openInBrowser() {
    if (hasOpenedExternalBrowser) return
    hasOpenedExternalBrowser = true

    const url = window.location.href
    const isAndroid = /Android/i.test(navigator.userAgent)
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
    const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname)
    const isIntentUrl = url.startsWith('intent://')

    if (isLocalhost) return

    if (isAndroid && !isIntentUrl) {
        window.location.href = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;end`
        return
    }

    if (isIOS) {
        window.open(url, '_system')
        return
    }

    window.open(url, '_blank')
}

function mapFeelgreatToCountry(fg: TCountryFeelgreat): TCountry {
    return {
        // Use the alpha-3 code (short) as the canonical country id. The FG
        // countries API is inconsistent — `country` is the alpha-3 for some
        // markets (OMN) but the display name for others (Taiwan) — whereas
        // `short` is always the alpha-3. Everything downstream treats
        // country.country as the alpha-3, so normalise it here.
        country: fg.short,
        roman_name: fg.roman_name,
        native_name: fg.native_name,
        test: fg.test,
        live: fg.live,
        short: fg.short,
        shorter: fg.shorter,
        maintenance_mode: fg.maintenance_mode ?? { maintenance_mode: false },
        flagUrl: fg.flagUrl,
        config: {
            currency: {
                code: fg.config.currency.full_currency_code || fg.config.currency.currency_code.english,
                name: fg.config.currency.currency_code.english
            },
            languageSettings: fg.config.languageSettings
        },
        payment_options: fg.payment_options
    }
}

function App() {
    const [{ isInApp }] = useState(() => InAppSpy())
    const { authenSso } = useAuthentication({
        enablePersist: true,
        // Refresh failed (token rejected / non-2xx): the hook clears the stale
        // session, then we force a guest reload so nothing runs authenticated.
        onRefreshError: () => window.location.replace('/')
    })
    const { getDictionary } = useDictionary()
    const setLanguage = useSetAtom(languageState)
    const language = useAtomValue(languageState)

    const [country, setCountry] = useAtom(countryState)
    const setFeelgreatCountry = useSetAtom(feelgreatCountryAtom)
    const [fgCountries, setFgCountries] = useAtom(feelgreatCountriesAtom)
    const [, setIsLoading] = useAtom(isLoadingFeelgreatCountriesAtom)
    const allCountries = useAtomValue(allFeelgreatCountriesAtom)

    const setFeelgreatSettings = useSetAtom(feelgreatSettingsAtom)
    const setIsFgLoading = useSetAtom(isFeelGreatLoadingAtom)
    const setSelectedFlavor = useSetAtom(selectedFlavorAtom)
    const setSelectedPurchaseOption = useSetAtom(selectedPurchaseOptionAtom)
    const setApiLoaded = useSetAtom(setApiLoadedAtom)
    const isWebReady = useAtomValue(isWebReadyAtom)

    const authentication = useAtomValue(authenticationState)

    const hasInitializedRef = useRef(false)
    const hasFetchedCountriesRef = useRef(false)

    // Session-wide analytics params: every event carries these, so no call site threads
    // them through. Set once the countries API resolves the alpha-3 (initAnalytics already
    // registered the build target as `market`).
    useEffect(() => {
        if (country.short) analytics.setContext({ country: country.short, language })
    }, [country.short, language])

    // Attach user_id and is_guest to analytics for the WHOLE session, not just the moment of
    // login. useLogin identifies on the login action, but a reload restores the session through
    // authenSso without re-running that path — so every event after a refresh would go out
    // with no user_id. Keyed on baId, so it also fires on logout (the atoms clear) and flips
    // is_guest back — which is what lets a page view be attributed to guest vs member at all.
    useEffect(() => {
        const isSignedIn = Boolean(authentication.token && authentication.baId)
        analytics.setContext({ is_guest: !isSignedIn })
        if (isSignedIn) analytics.identify(authentication.baId)
    }, [authentication.token, authentication.baId])

    useEffect(() => {
        if (isInApp) openInBrowser()
    }, [isInApp])

    useEffect(() => {
        if (fgCountries || hasFetchedCountriesRef.current) return
        hasFetchedCountriesRef.current = true
        setIsLoading(true)

        directGet<TFeelGreatCountriesResponse>(getFeelGreatCountriesUrl())
            .then(data => {
                setFgCountries(data)
            })
            .catch(err => {
                console.error('Failed to fetch FeelGreat countries:', err)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [fgCountries, setFgCountries, setIsLoading])

    useEffect(() => {
        if (allCountries.length === 0 || country.country.length > 0 || hasInitializedRef.current) return
        hasInitializedRef.current = true

        const countrySlug = (__COUNTRY__ as string).toLowerCase()
        const foundCountry = allCountries.find(
            c =>
                c.roman_name?.toLowerCase() === countrySlug ||
                c.country?.toLowerCase() === countrySlug ||
                c.shorter?.toLowerCase() === countrySlug ||
                c.short?.toLowerCase() === countrySlug
        )
        if (foundCountry) {
            setFeelgreatCountry(foundCountry)
            setCountry(mapFeelgreatToCountry(foundCountry))
            setApiLoaded({ key: 'country', value: true }) // KEY 1
            getDictionary({ countryCode: foundCountry.short, web: 'feelGreat' })

            const urlLang = new URLSearchParams(window.location.search).get('lang')
            const storedLang = (() => {
                try {
                    return localStorage.getItem('fg_language')
                } catch {
                    return null
                }
            })()
            const langCode = urlLang?.toUpperCase() || storedLang?.toUpperCase() || foundCountry.config.languageSettings.default?.toUpperCase() || 'EN'
            try {
                localStorage.setItem('fg_language', langCode.toLowerCase())
            } catch {
                /* ignore */
            }
            setLanguage(langCode)

            // KEY 3 — restore persisted session + refresh its token in the
            // background. Pass the resolved alpha3 explicitly: the `country`
            // atom isn't populated yet in this closure, so without it the
            // refreshToken call bails on an empty country code.
            authenSso(foundCountry.short).finally(() => {
                setApiLoaded({ key: 'refreshToken', value: true })
            })

            const storedStatus = readStoredUserStatus()
            const initialBaStatus = storedStatus ?? (foundCountry.short === 'JPN' ? 'C' : undefined)
            const needsOrderCalc = !!storedStatus && (ORDERCALC_COUNTRIES as readonly string[]).includes(foundCountry.short)
            const storedBaId = needsOrderCalc ? (JSON.parse(localStorage.getItem('u25_authentication') ?? '{}') as { baId?: string }).baId : undefined

            setIsFgLoading(true)
            fetchFeelGreatSettings({
                countryAlpha3: foundCountry.short,
                baStatus: initialBaStatus,
                ordercalC: needsOrderCalc,
                baId: storedBaId
            })
                .then(data => {
                    setFeelgreatSettings(data)
                    if (data.defaultValues?.flavor) setSelectedFlavor(data.defaultValues.flavor)
                    if (data.defaultValues?.purchaseOption) setSelectedPurchaseOption(data.defaultValues.purchaseOption)
                    setApiLoaded({ key: 'settings', value: true }) // KEY 2 (success)
                })
                .catch(err => {
                    console.error('Failed to fetch FeelGreat settings:', err)
                    setApiLoaded({ key: 'settings', value: true }) // KEY 2 (failure — unblock anyway)
                })
                .finally(() => setIsFgLoading(false))
        }
    }, [
        allCountries,
        country.country.length,
        setCountry,
        setFeelgreatCountry,
        authenSso,
        getDictionary,
        setApiLoaded,
        setFeelgreatSettings,
        setIsFgLoading,
        setLanguage,
        setSelectedFlavor,
        setSelectedPurchaseOption
    ])

    return (
        <ThemeProvider theme={feelGreatTheme} data-testid="app-theme-provider">
            <ErrorBoundary fallback="page" data-testid="app-error-boundary">
                <LoadingBar data-testid="app-loading-bar" />
                <Stack sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }} bgcolor="background.default" data-testid="app-stack-font-loader">
                    <FontLoader fonts={['inter', 'poppins', 'prompt']} data-testid="app-font-loader" />
                    <Box sx={{ flex: 1 }} data-testid="app-box">
                        {isWebReady && <AppRouter data-testid="app-app-router" />}
                    </Box>
                </Stack>
                <EnvironmentSwitcher data-testid="app-environment-switcher" />
            </ErrorBoundary>
        </ThemeProvider>
    )
}

export default App
